import React from "react";

const partners = [
  {
    name: "Cloudflare",
    icon: "☁️",
    category: "Infrastructure Partner",
    summary: "Global edge platform powering compute, database, and storage.",
    stack: ["Workers", "D1", "R2", "Zero Trust"],
    details: [
      "Workers for globally distributed API execution",
      "D1 for low-latency edge-native relational storage",
      "R2 for durable object storage without egress complexity",
    ],
  },
  {
    name: "Stripe",
    icon: "💳",
    category: "Payments Partner",
    summary: "Secure billing and subscription management at global scale.",
    stack: ["Checkout", "Billing", "Webhooks", "Radar"],
    details: [
      "PCI DSS Level 1 payment processing",
      "Subscription lifecycle management and invoicing",
      "Reliable webhooks for billing state synchronization",
    ],
  },
  {
    name: "React",
    icon: "⚛️",
    category: "Application Platform",
    summary: "Component architecture that keeps our UI fast and maintainable.",
    stack: ["Hooks", "Router", "Reusable UI", "State"],
    details: [
      "Reusable design system components",
      "Predictable rendering model for complex tools",
      "Large ecosystem and long-term maintainability",
    ],
  },
  {
    name: "TypeScript",
    icon: "🧩",
    category: "Engineering Foundation",
    summary: "Type safety across frontend and backend reduces production risk.",
    stack: ["Types", "Schema Contracts", "API Safety", "Refactoring"],
    details: [
      "Stronger compile-time guarantees",
      "Safer refactors as platform complexity grows",
      "Clear contracts for APIs and tool schemas",
    ],
  },
];

export default function Partners() {
  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-indigo-300 font-semibold uppercase tracking-widest text-sm mb-5">Partners</p>
          <h1 className="text-5xl font-extrabold mb-6 leading-tight">Built with trusted technology partners</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Our platform is powered by infrastructure and tools selected for reliability, security, and long-term scalability.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-7">
          {partners.map((p) => (
            <article key={p.name} className="border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-md transition bg-white">
              <p className="text-xs font-bold bg-indigo-50 text-indigo-600 inline-block px-2.5 py-1 rounded-full mb-4">{p.category}</p>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2 flex items-center gap-2">
                <span className="text-2xl" aria-hidden="true">{p.icon}</span>
                <span>{p.name}</span>
              </h2>
              <p className="text-gray-500 text-sm mb-4">{p.summary}</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {p.stack.map((item: string) => (
                  <span key={item} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {item}
                  </span>
                ))}
              </div>
              <ul className="space-y-2">
                {p.details.map((d) => (
                  <li key={d} className="flex gap-2 text-sm text-gray-600">
                    <span className="text-indigo-500">→</span>
                    {d}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Partner with us</h2>
          <p className="text-gray-500 mb-6">
            We are open to strategic partnerships in infrastructure, AI tooling, and vertical integrations.
          </p>
          <a href="mailto:[email protected]" className="inline-block bg-indigo-600 text-white font-bold px-8 py-3.5 rounded-xl hover:bg-indigo-700 transition">
            Contact Partnerships →
          </a>
        </div>
      </section>
    </div>
  );
}
